import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory } from "react-router";
import { SortableHandle } from "react-sortable-hoc";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import actions from "../../lib/actions";
import SimpleCrud from "../shared/crud/simple-crud";
import { messages } from "./messages";
import { SECTION } from "../../lib/constants";
import { DefaultTableRowActionsProps, Delete } from "../shared/actions/tableActions";
import { StyledSwapArrow } from "../extras/styles";
import { faCopy, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { InsuranceEntity } from "./entity";
import { useTableColumns } from "./hooks/tableColumns.hook";
import { useSchema } from "./hooks/schema.hook";
import {
  useAuthorizedActions,
  withAuthorizationVerifier,
} from "../../components/authorization-verifier";
import { useInsuranceApi } from "./hooks/insuranceApi.hook";

function InsuranceCrud({ propertyTicker }: { propertyTicker: string; intl: IntlShape }) {
  const authorizedActions = useAuthorizedActions(SECTION.INSURANCES);
  const history = useHistory();
  const customSchema = useSchema();
  const tableColumns = useTableColumns();
  const { onGetOne, onGetAll, onDelete, onReOrder } = useInsuranceApi();

  const [reload, setReload] = useState(true);
  const [forceUpdate, setForceUpdate] = useState(0);

  const canCreate = authorizedActions.create && actions.CreateInsurances;

  const canUpdate = useMemo(() => !!authorizedActions.modify, [authorizedActions.modify]);

  const onView = useCallback(
    (id: string) => history.push(`/${propertyTicker}/insurance-edit/${id}`),
    [history, propertyTicker]
  );

  const onDuplicate = useCallback(
    (id: React.MouseEventHandler<HTMLElement>) =>
      history.push(`/${propertyTicker}/insurance-copy/${id}`),
    [history, propertyTicker]
  );

  const handleOnDelete = useCallback(
    async (id: string) => {
      await onDelete(id);
      setForceUpdate(forceUpdate + 1);
    },
    [forceUpdate, onDelete]
  );

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<any>) => {
      const DragButton = SortableHandle(() => (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={props => (
            <Tooltip id={`drag-tooltip-${row.id}`} {...props}>
              <FormattedMessage {...messages.drag} />
            </Tooltip>
          )}>
          <Button
            size="sm"
            className="mr-2"
            variant="light"
            disabled={!canUpdate}
            style={{ cursor: "move" }}>
            <StyledSwapArrow />
          </Button>
        </OverlayTrigger>
      ));

      return (
        <Table.Cell {...rest}>
          <DragButton />
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={props => (
              <Tooltip id={`duplicate-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...messages.duplicate} />
              </Tooltip>
            )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              disabled={!canUpdate}
              onClick={() => onDuplicate(row?.id)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={props => (
              <Tooltip id={`edit-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...messages.edit} />
              </Tooltip>
            )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              disabled={!canUpdate}
              onClick={() => onView(row?.id)}>
              <FontAwesomeIcon icon={faPencilAlt} />
            </Button>
          </OverlayTrigger>

          {canUpdate && (
            <Delete
              rest={rest}
              onDelete={handleOnDelete}
              row={row}
              onReloadList={() => setReload(reload)}
              useOverlay={true}
            />
          )}
        </Table.Cell>
      );
    },
    [canUpdate, handleOnDelete, onDuplicate, onView, reload]
  );

  const customActions = () => {
    if (canCreate) {
      return (
        <Link className="btn btn-primary" to="insurance-new">
          <FormattedMessage {...messages.create} />
        </Link>
      );
    }
    return <></>;
  };

  return (
    <SimpleCrud<InsuranceEntity>
      managerName={"insurances"}
      schema={customSchema}
      tableColumns={tableColumns}
      formConfiguration={{}}
      actions={customActions}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onReorder={onReOrder}
      section={SECTION.INSURANCES}
      canCreate={actions.CreateInsurances}
      canUpdate={actions.ModifyInsurances}
      hideExportButton={true}
      forceLoad={forceUpdate}
      rowActions={props => <TableRowActions {...props} />}
    />
  );
}

export default injectIntl(withAuthorizationVerifier(InsuranceCrud));
